import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  FormControl,
  FormLabel,
  FormErrorIcon,
  FormHelperText,
  FormErrorMessage,
  Input,
  VisuallyHidden
} from "@/storybook-components";
import type { ChangeEvent } from "react";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface EmailOrScreenNameQuestionProps {
  question: Partial<Question>;
}

export const validate = async (
  answer: string,
  question: Partial<Question>
): Promise<string[]> => {
  const schema = string().required();
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const EmailOrScreenNameQuestion = ({
  question
}: EmailOrScreenNameQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );
  };

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel>
        {translate("questions.login.emailOrScreenName")}
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <Input
        type="text"
        value={answer}
        onChange={handleChange}
        maxLength={question.maxLength}
        data-testid="emailOrScreenNameQuestion-input"
        size={{ base: "lg", lg: "md" }}
        name="emailOrScreenNameQuestion-input"
        autoComplete="off"
      />
      {error.length > 0 && (
        <FormErrorMessage data-testid="emailOrScreenNameQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default EmailOrScreenNameQuestion;
